import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import Loading from "../../../components/common/loading";
import RowList from "../../../components/common/rowList";
import TextInput from "../../../components/common/inputs/textInput.jsx";
import ToggleInput from "../../../components/common/inputs/toggleInput.jsx";
import SelectInput from "../../../components/common/inputs/selectInput.jsx";
import NotificationManager from "../../../utils/notificationManager";
import { isUnknownApiEndpointError } from "../../../utils/networkErrorUtils";
import {
  clearNetworkStatusCacheForTests,
  fetchNetworkStatusWithCache,
  readNetworkStatusCache,
  writeNetworkStatusCache,
} from "../../../utils/networkStatusCache";
import {
  buildInterfaceRows,
  getActiveUplinkSummaryFromConf,
  getPrimaryInterfaceSummary,
  interfacesMatch,
} from "../../../utils/networkInterfaces";

export {
  clearNetworkStatusCacheForTests as __clearNetworkStatusCacheForTests,
};

const styles = {
  wrap: {
    background: "var(--surface-color)",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    padding: "24px",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 600,
    color: "var(--text-color)",
  },
  description: {
    marginTop: "8px",
    color: "var(--text-muted-color)",
    fontSize: "14px",
    lineHeight: 1.6,
  },
  refreshButton: {
    border: 0,
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    color: "var(--text-color)",
  },
  banner: {
    marginBottom: "16px",
    padding: "12px 16px",
    borderRadius: 6,
    fontSize: "13px",
  },
  warningBanner: {
    background: "var(--warning-background)",
    color: "var(--warning-color)",
  },
  infoBanner: {
    background: "var(--app-surface-3)",
    color: "var(--app-text-muted)",
    border: "1px solid var(--app-border)",
  },
  errorBanner: {
    background: "var(--danger-background)",
    color: "var(--danger-color)",
  },
  section: {
    borderTop: "1px solid var(--border-color)",
    paddingTop: "22px",
    marginTop: "22px",
  },
  firstSection: {
    paddingTop: 0,
    marginTop: 0,
    borderTop: 0,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "14px",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "var(--text-color)",
  },
  sectionDescription: {
    margin: "4px 0 0",
    color: "var(--text-muted-color)",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "18px",
    alignItems: "flex-start",
  },
  panelGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "18px",
    alignItems: "flex-start",
  },
  panel: {
    minWidth: 0,
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "8px",
  },
  panelTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: 600,
    color: "var(--text-color)",
  },
  formPanel: {
    display: "grid",
    gap: "14px",
    alignContent: "flex-start",
  },
  formField: {
    background: "var(--app-surface-2)",
    borderRadius: 8,
    padding: "18px 20px",
  },
  configGroup: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "1px solid var(--app-border)",
  },
  firstConfigGroup: {
    marginTop: 0,
    paddingTop: 0,
    borderTop: 0,
  },
  formFields: {
    display: "grid",
    rowGap: "18px",
  },
  compactActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-muted-color)",
    fontWeight: 600,
  },
  td: {
    padding: "9px 10px",
    borderBottom: "1px solid var(--border-color)",
    verticalAlign: "top",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: "11px",
    fontWeight: 600,
    background: "var(--background-color)",
    border: "1px solid var(--border-color)",
  },
  formActions: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: "12px",
  },
  button: {
    border: 0,
    borderRadius: 4,
    padding: "8px 14px",
    cursor: "pointer",
    background: "var(--primary-color)",
    color: "var(--primary-font-color)",
    fontWeight: 600,
  },
  secondaryButton: {
    border: "1px solid var(--border-color)",
    borderRadius: 4,
    padding: "7px 13px",
    cursor: "pointer",
    background: "var(--surface-color)",
    color: "var(--text-color)",
    fontWeight: 600,
  },
  dangerButton: {
    border: 0,
    borderRadius: 4,
    padding: "8px 14px",
    cursor: "pointer",
    background: "var(--danger-color)",
    color: "#fff",
    fontWeight: 600,
  },
  scanList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px",
    marginTop: "12px",
  },
  scanItem: {
    border: "1px solid var(--border-color)",
    borderRadius: 6,
    padding: "10px 12px",
    background: "var(--background-color)",
    textAlign: "left",
    cursor: "pointer",
  },
  muted: {
    color: "var(--text-muted-color)",
    fontSize: "12px",
  },
  fieldStack: {
    display: "grid",
    gap: "14px",
    alignContent: "flex-start",
  },
  signalValue: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  overviewHero: {
    border: "1px solid var(--border-color)",
    borderRadius: 8,
    background: "var(--app-surface-2)",
    padding: "16px 18px",
    marginBottom: "18px",
  },
  overviewHeroTitle: {
    margin: 0,
    color: "var(--text-color)",
    fontSize: "17px",
    fontWeight: 600,
  },
  overviewHeroDetail: {
    margin: "6px 0 0",
    color: "var(--text-muted-color)",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  overviewTileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
  },
  overviewTile: {
    background: "var(--app-surface-1)",
    border: "1px solid var(--app-border)",
    borderRadius: 8,
    padding: "14px 16px",
    minHeight: "86px",
  },
  overviewTileLabel: {
    margin: 0,
    color: "var(--text-muted-color)",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0,
  },
  overviewTileValue: {
    margin: "8px 0 0",
    color: "var(--text-color)",
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: 1.35,
  },
  overviewTileDetail: {
    margin: "4px 0 0",
    color: "var(--text-muted-color)",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  connectionList: {
    display: "grid",
    gap: "10px",
  },
  connectionItem: {
    display: "grid",
    gridTemplateColumns: "minmax(170px, 1.1fr) minmax(120px, 0.7fr) minmax(180px, 1.4fr)",
    gap: "12px",
    alignItems: "center",
    border: "1px solid var(--border-color)",
    borderRadius: 8,
    padding: "12px 14px",
    background: "var(--surface-color)",
  },
  connectionName: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    color: "var(--text-color)",
    fontWeight: 600,
    fontSize: "13px",
  },
  connectionRole: {
    color: "var(--text-muted-color)",
    fontSize: "12px",
  },
  connectionDetail: {
    color: "var(--text-color)",
    fontSize: "13px",
    lineHeight: 1.45,
  },
};

const NETWORK_VIEW = {
  OVERVIEW: "overview",
  WIRED: "wired",
  LTE: "lte",
  WIFI: "wifi",
};

const ltePolicyOptions = [
  { label: "Ethernet primary, LTE fallback", value: "lteFallback" },
  { label: "LTE primary, Ethernet fallback", value: "lteOnly" },
];

const ipAssignOptions = [
  { label: "DHCP", value: "dhcp" },
  { label: "Static", value: "static" },
];

const usbModeOptions = [
  { label: "Disabled", value: "disabled" },
  { label: "Front + USB bridge", value: "bridge_mode" },
  { label: "Secondary network interface", value: "secondary_nic" },
];

const valueOrDash = (value) =>
  value === undefined || value === null || value === ""
    ? "-"
    : typeof value === "boolean"
      ? value
        ? "Yes"
        : "No"
      : value;

const parseSignalNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const match = String(value).match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

const signalToBars = (value) => {
  const signal = parseSignalNumber(value);
  if (signal === null) return null;

  if (signal >= 0 && signal <= 5) {
    return Math.max(0, Math.min(5, Math.round(signal)));
  }

  if (signal >= -65) return 5;
  if (signal >= -75) return 4;
  if (signal >= -85) return 3;
  if (signal >= -95) return 2;
  if (signal >= -105) return 1;
  return 0;
};

const formatSignalValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  const text = String(value).trim();
  if (!text) return "-";
  if (text.toLowerCase().includes("dbm")) return text;
  const numeric = parseSignalNumber(text);
  if (numeric !== null && numeric < 0) return `${numeric} dBm`;
  return text;
};

const formatSignalSummary = (value) => {
  const formatted = formatSignalValue(value);
  return formatted === "-" ? "" : formatted;
};

const SignalBars = ({ value }) => {
  const bars = signalToBars(value);
  if (bars === null) return null;
  const heights = [6, 9, 12, 15, 18];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "flex-end",
        gap: "3px",
        minHeight: "18px",
      }}
      title={`${bars}/5 bars`}
      aria-label={`${bars} out of 5 signal bars`}
    >
      {heights.map((height, index) => (
        <span
          key={height}
          style={{
            display: "inline-block",
            width: "5px",
            height,
            borderRadius: 999,
            backgroundColor:
              index < bars ? "var(--primary-color)" : "var(--border-color)",
          }}
        />
      ))}
    </span>
  );
};

const SignalValue = ({ value, fallback = "-" }) => {
  const formatted = formatSignalValue(value);
  if (formatted === "-") return fallback;
  return (
    <span style={styles.signalValue}>
      <SignalBars value={value} />
      <span>{formatted}</span>
    </span>
  );
};

const frequencyToBand = (frequency) => {
  const freq = Number(frequency);
  if (!Number.isFinite(freq)) return "";
  if (freq >= 5925) return "6 GHz";
  if (freq >= 4900) return "5 GHz";
  if (freq >= 2400) return "2.4 GHz";
  return "";
};

const formatSecurityFromFlags = (flags) => {
  const normalized = String(flags || "").toUpperCase();
  if (!normalized) return "";
  if (normalized.includes("WEP")) return "WEP";
  if (normalized.includes("SAE") && normalized.includes("PSK")) return "WPA2/WPA3";
  if (normalized.includes("SAE")) return "WPA3";
  if (normalized.includes("WPA") || normalized.includes("RSN")) return "WPA/WPA2";
  return "Open";
};

const getHealthToneStyle = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (
    normalized.includes("up") ||
    normalized.includes("healthy") ||
    normalized.includes("connected") ||
    normalized.includes("registered") ||
    normalized.includes("enabled")
  ) {
    return {
      color: "var(--success-color)",
      borderColor: "rgba(46, 204, 113, 0.35)",
      background: "rgba(46, 204, 113, 0.1)",
    };
  }
  if (
    normalized.includes("down") ||
    normalized.includes("unhealthy") ||
    normalized.includes("failed") ||
    normalized.includes("error")
  ) {
    return {
      color: "var(--danger-color)",
      borderColor: "rgba(231, 76, 60, 0.35)",
      background: "rgba(231, 76, 60, 0.1)",
    };
  }
  return {};
};

const safeConf = (conf) =>
  conf && typeof conf === "object" && !Array.isArray(conf) ? conf : {};

const cleanStatusText = (value) =>
  value === undefined || value === null
    ? ""
    : String(value).trim().replace(/^"+|"+$/g, "");

const formatAddressMode = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "dhcp") return "DHCP";
  if (normalized === "static") return "Static";
  return valueOrDash(value);
};

const formatUsbMode = (value) => {
  if (value === "bridge_mode") return "Front + USB bridge";
  if (value === "secondary_nic") return "Secondary network interface";
  if (value === "disabled") return "Disabled";
  return valueOrDash(value);
};

const detailJoin = (parts) =>
  parts
    .map((part) =>
      part === undefined || part === null ? "" : String(part).trim()
    )
    .filter(Boolean)
    .join(" · ");

const formatMacDetail = (value) => {
  const mac = String(value || "").trim();
  if (!mac || /^00(?::00){5}$/i.test(mac)) return "";
  return `MAC ${mac}`;
};

const textIncludes = (value, needles = []) => {
  const normalized = String(value || "").toLowerCase();
  return needles.some((needle) => normalized.includes(needle));
};

const isEnabledValue = (value) => {
  if (typeof value === "string") {
    return ["true", "1", "enabled", "yes"].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

const getNetworkStatusRoutes = (status = null) => {
  const payload = status?.wanHealth?.data || status?.wanHealth || {};
  if (Array.isArray(payload.routes)) return payload.routes;
  if (Array.isArray(payload.config?.routes)) return payload.config.routes;
  return [];
};

const hasReportedActiveRoute = (cfg = {}, status = null, cellular = {}) =>
  Boolean(
    cfg.currentDefaultRoute ||
      cfg.defaultNetworkRouteInterface ||
      cellular.defaultNetworkRouteInterface ||
      status?.activeUplink?.interfaceName ||
      status?.activeUplink?.runtimeInterfaceName
  );

const getRouteEvidence = (cfg = {}, status = null, cellular = {}) => {
  if (hasReportedActiveRoute(cfg, status, cellular)) return "reported";
  if (getNetworkStatusRoutes(status).length) return "monitored";
  return "configured";
};

const formatLtePolicy = (policy) => {
  if (policy === "lteOnly") return "LTE primary, ethernet fallback";
  if (policy === "lteFallback") return "Ethernet primary, LTE fallback";
  return "Ethernet primary";
};

const summarizeInterfaceReadiness = (row, fallback = "Unknown") => {
  const health = row?.health || fallback;
  if (textIncludes(health, ["down", "unavailable", "disabled", "failed"])) {
    return health;
  }
  if (textIncludes(health, ["degraded"])) {
    return health;
  }
  if (
    textIncludes(health, ["active", "healthy", "ready", "connected", "up"]) ||
    row?.ipAddress
  ) {
    return "Ready";
  }
  return health || fallback;
};

const summarizeLteReadiness = (cellular = {}, row = null, enabled = false) => {
  if (!enabled) return "Disabled";
  if (cellular.modemDetected === false) return "Unavailable";
  if (textIncludes(cellular.registrationStatus, ["registered"])) return "Ready";
  if (textIncludes(row?.health, ["active", "healthy", "ready", "registered"])) {
    return "Ready";
  }
  if (
    textIncludes(row?.health, ["unavailable", "not registered", "no lte route"]) ||
    textIncludes(row?.statusDetail, ["not detected", "not responding", "unavailable"])
  ) {
    return row?.health || "Unavailable";
  }
  return "Configured";
};

const buildShortInterfaceDetail = ({
  row,
  config,
  wifiStatus,
  cellularStatus,
}) => {
  if (!row) return "-";
  if (interfacesMatch(row.interfaceName, "lte0")) {
    if (cellularStatus.modemDetected === false) return "Modem not detected";
    return detailJoin([
      row.operator || cellularStatus.operator,
      formatSignalSummary(row.signalStrength ?? cellularStatus.signalStrength),
      row.registrationStatus || cellularStatus.registrationStatus,
    ]) || "LTE IP hidden";
  }
  if (interfacesMatch(row.interfaceName, "wlan0")) {
    return detailJoin([
      row.ssid || wifiStatus.ssid,
      row.ipAddress || wifiStatus.ipAddress,
      formatSignalSummary(row.signalStrength ?? wifiStatus.signalLevel),
      formatMacDetail(row.macAddress || wifiStatus.macAddress),
    ]) || "WiFi management";
  }
  if (interfacesMatch(row.interfaceName, "eth1")) {
    const macDetail = formatMacDetail(row.macAddress || config.secondaryMacAddr);
    if (config.secondaryNetworkInterfaceMode === "bridge_mode") {
      return detailJoin(["Bridged with Front Ethernet", macDetail]);
    }
    return detailJoin([
      row.ipAddress,
      formatAddressMode(config.secondaryIpAssign),
      macDetail,
    ]) || formatUsbMode(config.secondaryNetworkInterfaceMode || "disabled");
  }
  return detailJoin([
    row.ipAddress,
    formatAddressMode(config.ipAssign),
    formatMacDetail(row.macAddress || config.macAddr),
  ]);
};

const getConfiguredIp = ({
  assignment,
  staticIp,
  dhcpIp,
}) => {
  if (assignment === "dhcp") return dhcpIp || "-";
  if (assignment === "static") return staticIp || "-";
  return dhcpIp || staticIp || "-";
};

const makeWiredForm = (conf = {}) => {
  const cfg = safeConf(conf);
  return {
    hostName: cfg.hostName || "",
    ipAssign: cfg.ipAssign || "dhcp",
    staticIp: cfg.staticIp || "",
    subnetMask: cfg.subnetMask || "",
    gatewayIp: cfg.gatewayIp || "",
    secondaryNetworkInterfaceMode:
      cfg.secondaryNetworkInterfaceMode || "disabled",
    secondaryHostName: cfg.secondaryHostName || "",
    secondaryIpAssign: cfg.secondaryIpAssign || "static",
    secondaryStaticIp: cfg.secondaryStaticIp || "",
    secondarySubnetMask: cfg.secondarySubnetMask || "",
    secondaryGatewayIp: cfg.secondaryGatewayIp || "",
  };
};

const makeLteForm = (conf = {}) => {
  const cfg = safeConf(conf);
  return {
    enableCellular: Boolean(cfg.enableCellular),
    cellularApn: cfg.cellularApn || cfg.apn || "",
    ltePolicy: cfg.ltePolicy || "lteFallback",
    primaryDns: cfg.primaryDns || "",
    secondaryDns: cfg.secondaryDns || "",
  };
};

export default function DeviceConfigurationNetwork({
  section,
  device,
  conf,
  confLoading,
  refreshConf,
  user,
  deviceOffline,
  offlineReason,
  hideHeader = false,
}) {
  const serial = device?.enclosureSerialNumber;
  const networkView = section?.networkView || NETWORK_VIEW.OVERVIEW;
  const initialNetworkStatusCache = readNetworkStatusCache(serial);
  const [networkStatus, setNetworkStatus] = useState(
    () => initialNetworkStatusCache?.data || null
  );
  const [networkLoading, setNetworkLoading] = useState(false);
  const [networkError, setNetworkError] = useState("");
  const [networkUnavailable, setNetworkUnavailable] = useState(
    () => Boolean(initialNetworkStatusCache?.unavailable)
  );
  const [wiredForm, setWiredForm] = useState(makeWiredForm(conf));
  const [wiredSaving, setWiredSaving] = useState(false);
  const [lteForm, setLteForm] = useState(makeLteForm(conf));
  const [lteSaving, setLteSaving] = useState(false);
  const [wifiNetworks, setWifiNetworks] = useState([]);
  const [wifiSsid, setWifiSsid] = useState("");
  const [wifiPassword, setWifiPassword] = useState("");
  const [wifiLoading, setWifiLoading] = useState(false);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }),
    []
  );

  const canModify =
    (user?.role?.editDeviceInfo ?? true) !== false && !deviceOffline;
  const networkStatusPending =
    Boolean(serial) && !networkStatus && !networkUnavailable && !networkError;
  const statusPending =
    confLoading || (networkLoading && !networkStatus) || networkStatusPending;
  const config = useMemo(() => safeConf(conf), [conf]);
  const hasLteFeature =
    Object.prototype.hasOwnProperty.call(config, "enableCellular") ||
    Boolean(config.ltePolicy) ||
    Boolean(networkStatus?.cellular?.available);
  const cellularStatus = networkStatus?.cellular?.data || {};
  const wifiStatus = networkStatus?.wifi?.data || {};
  const wifiAvailable = Boolean(networkStatus?.wifi?.available);
  const wifiRadioEnabled =
    wifiStatus.enabled !== undefined
      ? Boolean(wifiStatus.enabled)
      : true;
  const interfaceRows = useMemo(
    () => buildInterfaceRows(networkStatus, config),
    [networkStatus, config]
  );
  const activeUplink = useMemo(
    () => getActiveUplinkSummaryFromConf(config, networkStatus),
    [config, networkStatus]
  );
  const primaryInterface = useMemo(
    () => getPrimaryInterfaceSummary(config, networkStatus),
    [config, networkStatus]
  );

  useEffect(() => {
    setLteForm(makeLteForm(conf));
    setWiredForm(makeWiredForm(conf));
  }, [conf]);

  const fetchNetworkStatus = useCallback(async ({ force = false } = {}) => {
    if (!serial) return;

    const cached = readNetworkStatusCache(serial);
    if (!force && cached?.fresh && (cached.data || cached.unavailable)) {
      setNetworkStatus(cached.data || null);
      setNetworkUnavailable(Boolean(cached.unavailable));
      setNetworkError("");
      return;
    }

    if (!force && cached && (cached.data || cached.unavailable)) {
      setNetworkStatus(cached.data || null);
      setNetworkUnavailable(Boolean(cached.unavailable));
      setNetworkError("");
    }

    setNetworkLoading(true);
    setNetworkError("");
    try {
      const result = await fetchNetworkStatusWithCache({
        serial,
        headers: authHeaders,
        force,
      });
      if (result?.unavailable) {
        setNetworkUnavailable(true);
        setNetworkStatus(null);
        return;
      }
      const nextStatus = result?.data || {};
      setNetworkStatus(nextStatus);
      setNetworkUnavailable(false);
    } catch (err) {
      if (isUnknownApiEndpointError(err)) {
        setNetworkUnavailable(true);
        setNetworkError("");
        setNetworkStatus(null);
        writeNetworkStatusCache(serial, {
          data: null,
          unavailable: true,
        });
        return;
      }
      const message =
        err?.response?.data?.message ||
        "Unable to retrieve network status from the device.";
      setNetworkError(message);
      NotificationManager.alert(message);
    } finally {
      setNetworkLoading(false);
    }
  }, [serial, authHeaders]);

  useEffect(() => {
    fetchNetworkStatus();
  }, [fetchNetworkStatus]);

  const handleRefresh = () => {
    if (refreshConf) {
      refreshConf({
        useCache: false,
        force: true,
        device,
      });
    }
    fetchNetworkStatus({ force: true });
  };

  const handleSaveWiredSettings = async () => {
    if (!serial || wiredSaving) return;

    const initial = makeWiredForm(conf);
    const payload = {};
    const maybeAdd = (key) => {
      if (wiredForm[key] !== initial[key]) {
        payload[key] = wiredForm[key];
      }
    };

    maybeAdd("hostName");
    maybeAdd("ipAssign");
    if (wiredForm.ipAssign === "static") {
      maybeAdd("staticIp");
      maybeAdd("subnetMask");
      maybeAdd("gatewayIp");
    }

    maybeAdd("secondaryNetworkInterfaceMode");
    if (wiredForm.secondaryNetworkInterfaceMode === "secondary_nic") {
      maybeAdd("secondaryHostName");
      maybeAdd("secondaryIpAssign");
      if (wiredForm.secondaryIpAssign === "static") {
        maybeAdd("secondaryStaticIp");
        maybeAdd("secondarySubnetMask");
        maybeAdd("secondaryGatewayIp");
      }
    }

    if (!Object.keys(payload).length) {
      NotificationManager.alert("No wired network setting changes detected.");
      return;
    }

    if (
      !window.confirm(
        "Changing wired network settings can temporarily interrupt device connectivity. Continue?"
      )
    ) {
      return;
    }

    try {
      setWiredSaving(true);
      await axios.put(`/v1/conf/${serial}`, payload, {
        headers: authHeaders,
      });
      NotificationManager.success("Wired network settings updated.");
      if (refreshConf) {
        refreshConf({ useCache: false, force: true, device });
      }
      fetchNetworkStatus({ force: true });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Failed to update wired network settings.";
      NotificationManager.alert(message);
    } finally {
      setWiredSaving(false);
    }
  };

  const handleSaveLteSettings = async () => {
    if (!serial || lteSaving) return;

    const payload = {};
    const initial = makeLteForm(conf);
    ["enableCellular", "cellularApn", "ltePolicy", "primaryDns", "secondaryDns"].forEach(
      (key) => {
        if (lteForm[key] !== initial[key]) {
          payload[key] = lteForm[key];
        }
      }
    );

    if (!Object.keys(payload).length) {
      NotificationManager.alert("No LTE setting changes detected.");
      return;
    }

    const disruptive =
      payload.enableCellular !== undefined || payload.ltePolicy !== undefined;
    if (
      disruptive &&
      !window.confirm(
        "Changing LTE availability or failover priority can temporarily interrupt device connectivity. Continue?"
      )
    ) {
      return;
    }

    try {
      setLteSaving(true);
      await axios.put(`/v1/conf/${serial}`, payload, {
        headers: authHeaders,
      });
      NotificationManager.success("LTE network settings updated.");
      if (refreshConf) {
        refreshConf({ useCache: false, force: true, device });
      }
      fetchNetworkStatus({ force: true });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Failed to update LTE network settings.";
      NotificationManager.alert(message);
    } finally {
      setLteSaving(false);
    }
  };

  const handleWifiScan = async () => {
    if (!serial || wifiLoading) return;
    setWifiLoading(true);
    try {
      const { data } = await axios.get(
        `/v1/devices/${serial}/network/wifi/scan`,
        { headers: authHeaders }
      );
      if (data?.data?.available === false) {
        NotificationManager.alert(
          data.data.message || "WiFi scan is not available on this firmware."
        );
        setWifiNetworks([]);
        return;
      }
      setWifiNetworks(data?.data?.networks || []);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to scan WiFi networks.";
      NotificationManager.alert(message);
    } finally {
      setWifiLoading(false);
    }
  };

  const handleWifiConnect = async () => {
    const ssid = wifiSsid.trim();
    if (!ssid || wifiLoading) {
      NotificationManager.alert("Select or enter a WiFi SSID.");
      return;
    }
    if (
      !window.confirm(
        "Joining a WiFi network can change management connectivity. Continue?"
      )
    ) {
      return;
    }

    setWifiLoading(true);
    try {
      const { data } = await axios.post(
        `/v1/devices/${serial}/network/wifi/connect`,
        {
          wifiSSID: ssid,
          wifiPassword,
          force: true,
        },
        { headers: authHeaders }
      );
      if (data?.data?.available === false) {
        NotificationManager.alert(
          data.data.message || "WiFi connect is not available on this firmware."
        );
        return;
      }
      NotificationManager.success("WiFi connection request sent.");
      setWifiPassword("");
      fetchNetworkStatus({ force: true });
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to connect WiFi network.";
      NotificationManager.alert(message);
    } finally {
      setWifiLoading(false);
    }
  };

  const handleWifiDisconnect = async () => {
    if (
      wifiLoading ||
      !window.confirm(
        "Disconnect WiFi from the current network? Ethernet and LTE connectivity are not changed."
      )
    ) {
      return;
    }

    setWifiLoading(true);
    try {
      const { data } = await axios.post(
        `/v1/devices/${serial}/network/wifi/disconnect`,
        {},
        { headers: authHeaders }
      );
      if (data?.data?.available === false) {
        NotificationManager.alert(
          data.data.message || "WiFi disconnect is not available on this firmware."
        );
        return;
      }
      NotificationManager.success("WiFi disconnect request sent.");
      fetchNetworkStatus({ force: true });
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to disconnect WiFi network.";
      NotificationManager.alert(message);
    } finally {
      setWifiLoading(false);
    }
  };

  const handleWifiRadio = async (enabled) => {
    if (
      wifiLoading ||
      !window.confirm(
        `${enabled ? "Enable" : "Disable"} the WiFi radio on this PDU?`
      )
    ) {
      return;
    }

    setWifiLoading(true);
    try {
      const { data } = await axios.post(
        `/v1/devices/${serial}/network/wifi/radio`,
        { enabled },
        { headers: authHeaders }
      );
      if (data?.data?.available === false) {
        NotificationManager.alert(
          data.data.message || "WiFi radio control is not available on this firmware."
        );
        return;
      }
      NotificationManager.success(`WiFi radio ${enabled ? "enabled" : "disabled"}.`);
      fetchNetworkStatus({ force: true });
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to update WiFi radio state.";
      NotificationManager.alert(message);
    } finally {
      setWifiLoading(false);
    }
  };

  const lteStatusRows = [
    {
      label: "Cellular",
      value: valueOrDash(cellularStatus.cellularEnabled),
    },
    {
      label: "Modem",
      value: cellularStatus.modemDetected === undefined
        ? "-"
        : cellularStatus.modemDetected
          ? "Detected"
          : "Not detected",
    },
    { label: "Registration", value: valueOrDash(cellularStatus.registrationStatus) },
    { label: "Operator", value: valueOrDash(cellularStatus.operator) },
    {
      label: "Signal",
      valueComponent: (
        <SignalValue value={cellularStatus.signalStrength} />
      ),
    },
    { label: "Signal Quality", value: valueOrDash(cellularStatus.signalQuality) },
    { label: "Access Technology", value: valueOrDash(cellularStatus.accessTechnology) },
    {
      label: "Default Route",
      value: valueOrDash(
        cellularStatus.defaultNetworkRouteInterface || config.currentDefaultRoute
      ),
    },
    { label: "APN", value: valueOrDash(cellularStatus.apn || conf?.cellularApn) },
  ];

  const selectedWifiNetwork = wifiNetworks.find(
    (network) => network.ssid && network.ssid === wifiSsid
  );
  const selectedWifiSecurity = selectedWifiNetwork
    ? formatSecurityFromFlags(selectedWifiNetwork.flags)
    : "";
  const selectedWifiBand = selectedWifiNetwork
    ? frequencyToBand(selectedWifiNetwork.frequency)
    : "";
  const wifiStatusRows = [
    {
      label: "Radio",
      value: wifiStatus.enabled === undefined
        ? "Available"
        : wifiStatus.enabled
          ? "Enabled"
          : "Disabled",
    },
    {
      label: "Current Network",
      value: wifiStatus.connected ? valueOrDash(wifiStatus.ssid) : "Not connected",
    },
    { label: "State", value: valueOrDash(wifiStatus.state || (wifiStatus.connected ? "Connected" : "")) },
    {
      label: "Signal",
      valueComponent: (
        <SignalValue value={wifiStatus.signalLevel} />
      ),
    },
    { label: "WiFi MAC", value: valueOrDash(wifiStatus.macAddress) },
    { label: "Key Management", value: valueOrDash(wifiStatus.keyManagement) },
    { label: "IP Address", value: valueOrDash(wifiStatus.ipAddress) },
    { label: "Gateway", value: valueOrDash(wifiStatus.gateway) },
    { label: "Netmask", value: valueOrDash(wifiStatus.netmask) },
    { label: "DNS Server", value: valueOrDash(wifiStatus.dnsServer) },
  ];

  const frontEthernetRow = interfaceRows.find((item) =>
    interfacesMatch(item.interfaceName, "eth0")
  );
  const usbEthernetRow = interfaceRows.find((item) =>
    interfacesMatch(item.interfaceName, "eth1")
  );
  const lteRow = interfaceRows.find((item) =>
    interfacesMatch(item.interfaceName, "lte0")
  );
  const wifiRow = interfaceRows.find((item) =>
    interfacesMatch(item.interfaceName, "wlan0")
  );
  const frontEthernetStatusRows = [
    { label: "Role", value: valueOrDash(frontEthernetRow?.role) },
    {
      label: "Health",
      valueComponent: (
        <span
          style={{
            ...styles.pill,
            ...getHealthToneStyle(
              frontEthernetRow?.health || cleanStatusText(config.primaryInterfaceStatus)
            ),
          }}
        >
          {valueOrDash(
            frontEthernetRow?.health || cleanStatusText(config.primaryInterfaceStatus)
          )}
        </span>
      ),
    },
    {
      label: "Link",
      value: valueOrDash(
        frontEthernetRow?.linkSignal || cleanStatusText(config.primaryInterfaceStatus)
      ),
    },
    { label: "IP Assignment", value: formatAddressMode(config.ipAssign) },
    {
      label: "IP Address",
      value: getConfiguredIp({
        assignment: config.ipAssign,
        staticIp: config.staticIp,
        dhcpIp: config.dhcpAssignedIp,
      }),
    },
    {
      label: "Gateway",
      value: valueOrDash(
        config.ipAssign === "dhcp"
          ? config.dhcpGatewayIp || config.gatewayIp
          : config.gatewayIp
      ),
    },
    {
      label: "Subnet Mask",
      value: valueOrDash(
        config.ipAssign === "dhcp"
          ? config.dhcpSubnetMask || config.subnetMask
          : config.subnetMask
      ),
    },
    { label: "MAC Address", value: valueOrDash(config.macAddr) },
    { label: "Actual Link", value: valueOrDash(cleanStatusText(config.linkSpeedActual)) },
  ];
  const usbEthernetStatusRows = [
    { label: "Role", value: valueOrDash(usbEthernetRow?.role || "Management") },
    {
      label: "Mode",
      value: formatUsbMode(config.secondaryNetworkInterfaceMode || "disabled"),
    },
    {
      label: "Health",
      valueComponent: (
        <span
          style={{
            ...styles.pill,
            ...getHealthToneStyle(
              usbEthernetRow?.health ||
                cleanStatusText(config.secondInterfaceStatus) ||
                (config.secondaryNetworkInterfaceMode === "disabled"
                  ? "Disabled"
                  : "Not detected")
            ),
          }}
        >
          {valueOrDash(
            usbEthernetRow?.health ||
              cleanStatusText(config.secondInterfaceStatus) ||
              (config.secondaryNetworkInterfaceMode === "disabled"
                ? "Disabled"
                : "Not detected")
          )}
        </span>
      ),
    },
    {
      label: "Link",
      value: valueOrDash(
        usbEthernetRow?.linkSignal || cleanStatusText(config.secondInterfaceStatus)
      ),
      disabled: config.secondaryNetworkInterfaceMode === "disabled",
    },
    {
      label: "IP Assignment",
      value: formatAddressMode(config.secondaryIpAssign),
      disabled: config.secondaryNetworkInterfaceMode !== "secondary_nic",
    },
    {
      label: "IP Address",
      value: getConfiguredIp({
        assignment: config.secondaryIpAssign,
        staticIp: config.secondaryStaticIp,
        dhcpIp: config.secondaryDhcpAssignedIp,
      }),
      disabled: config.secondaryNetworkInterfaceMode !== "secondary_nic",
    },
    {
      label: "Gateway",
      value: valueOrDash(
        config.secondaryIpAssign === "dhcp"
          ? config.secondaryDhcpGatewayIp || config.secondaryGatewayIp
          : config.secondaryGatewayIp
      ),
      disabled: config.secondaryNetworkInterfaceMode !== "secondary_nic",
    },
    {
      label: "Subnet Mask",
      value: valueOrDash(
        config.secondaryIpAssign === "dhcp"
          ? config.secondaryDhcpSubnetMask || config.secondarySubnetMask
          : config.secondarySubnetMask
      ),
      disabled: config.secondaryNetworkInterfaceMode !== "secondary_nic",
    },
    {
      label: "MAC Address",
      value: valueOrDash(config.secondaryMacAddr),
      disabled: config.secondaryNetworkInterfaceMode !== "secondary_nic",
    },
  ];

  const routeEvidence = getRouteEvidence(config, networkStatus, cellularStatus);
  const routeEvidenceDetail =
    routeEvidence === "reported"
      ? "Reported by device"
      : routeEvidence === "monitored"
        ? "Inferred from WAN health"
        : hasLteFeature
          ? "Active route not reported by this firmware"
          : "";
  const hasEnableCellularFlag = Object.prototype.hasOwnProperty.call(
    config,
    "enableCellular"
  );
  const lteEnabledForOverview =
    hasLteFeature &&
    (hasEnableCellularFlag
      ? isEnabledValue(config.enableCellular)
      : cellularStatus.cellularEnabled !== false);
  const lteReadiness = summarizeLteReadiness(
    cellularStatus,
    lteRow,
    lteEnabledForOverview
  );
  const ethernetReadiness = summarizeInterfaceReadiness(
    frontEthernetRow,
    cleanStatusText(config.primaryInterfaceStatus) || "Unknown"
  );
  const wifiConnected =
    wifiAvailable &&
    (Boolean(wifiStatus.connected) ||
      textIncludes(wifiRow?.health, ["connected", "active", "healthy"]));
  const wifiActive =
    wifiConnected &&
    routeEvidence !== "configured" &&
    interfacesMatch(activeUplink?.interfaceName, "wlan0");
  const activeRouteIsLte = interfacesMatch(activeUplink?.interfaceName, "lte0");
  const activeRouteIsEthernet = interfacesMatch(activeUplink?.interfaceName, "eth0");
  const activeRouteIsWifi = interfacesMatch(activeUplink?.interfaceName, "wlan0");
  const primaryIsLte = interfacesMatch(primaryInterface?.interfaceName, "lte0");
  const frontEthernetIp =
    frontEthernetRow?.ipAddress ||
    getConfiguredIp({
      assignment: config.ipAssign,
      staticIp: config.staticIp,
      dhcpIp: config.dhcpAssignedIp,
    });
  const wifiIp = wifiRow?.ipAddress || wifiStatus.ipAddress || "";
  const activeWanPathLabel =
    routeEvidence === "configured" && hasLteFeature
      ? activeUplink?.label || primaryInterface?.label || "Configured policy"
      : activeUplink?.label || primaryInterface?.label || "Front Ethernet";
  const activeWanPathDetail = routeEvidenceDetail;
  const ltePolicyLabel = formatLtePolicy(config.ltePolicy);
  const frontEthernetLink =
    frontEthernetRow?.linkSignal ||
    cleanStatusText(config.primaryInterfaceStatus) ||
    "";
  const ethernetNeedsAttention =
    !frontEthernetIp ||
    textIncludes(ethernetReadiness, [
      "down",
      "unavailable",
      "failed",
      "degraded",
    ]);

  const overviewHero = (() => {
    if (hasLteFeature) {
      if (routeEvidence === "configured") {
        return {
          title: `${ltePolicyLabel}.`,
          detail:
            "This firmware does not report the active default route, so CMP is showing configured policy and available health signals.",
        };
      }
      if (config.ltePolicy === "lteFallback") {
        if (activeRouteIsLte) {
          return {
            title: "LTE fallback is active.",
            detail: "Ethernet is configured as primary but is not the current WAN path.",
          };
        }
        return {
          title: "Front Ethernet is active.",
          detail: `LTE fallback is ${String(lteReadiness).toLowerCase()}.`,
        };
      }
      if (config.ltePolicy === "lteOnly") {
        if (activeRouteIsEthernet) {
          return {
            title: "Ethernet fallback is active.",
            detail: "LTE is configured as primary but is not the current WAN path.",
          };
        }
        return {
          title: "LTE primary is active.",
          detail: `Ethernet fallback is ${String(ethernetReadiness).toLowerCase()}.`,
        };
      }
    }

    if (wifiActive || (wifiConnected && activeRouteIsWifi)) {
      return {
        title: "WiFi is the active network path.",
        detail: detailJoin([
          wifiStatus.ssid || wifiRow?.ssid,
          wifiIp,
          formatSignalSummary(wifiStatus.signalLevel || wifiRow?.signalStrength),
        ]),
      };
    }

    if (wifiConnected) {
      return {
        title: "Front Ethernet is the WAN path.",
        detail: `WiFi management is connected${wifiStatus.ssid ? ` to ${wifiStatus.ssid}` : ""}.`,
      };
    }

    if (textIncludes(ethernetReadiness, ["down", "unavailable", "failed"])) {
      return {
        title: "Front Ethernet needs attention.",
        detail: frontEthernetIp
          ? `${frontEthernetIp} · ${formatAddressMode(config.ipAssign)}`
          : "No usable Ethernet address is currently available.",
      };
    }

    return {
      title: "Front Ethernet is active.",
      detail: detailJoin([frontEthernetIp, formatAddressMode(config.ipAssign)]),
    };
  })();

  const overviewTiles = (() => {
    if (hasLteFeature) {
      const primaryReadiness = primaryIsLte ? lteReadiness : ethernetReadiness;
      const fallbackReadiness = primaryIsLte ? ethernetReadiness : lteReadiness;
      return [
        {
          label: "WAN Path",
          value: activeWanPathLabel,
          detail: activeWanPathDetail,
        },
        {
          label: "Configured Policy",
          value: ltePolicyLabel,
          detail: routeEvidence === "configured" ? routeEvidenceDetail : "",
        },
        {
          label: "Primary Health",
          value: primaryReadiness,
          detail: primaryInterface?.label,
        },
        {
          label: "Fallback Readiness",
          value: fallbackReadiness,
          detail: primaryIsLte ? "Front Ethernet" : "LTE (lte0)",
        },
      ];
    }

    if (wifiConnected && (wifiActive || activeRouteIsWifi)) {
      return [
        {
          label: "WAN Path",
          value: "WiFi",
          detail: routeEvidenceDetail,
        },
        {
          label: "SSID",
          value: wifiStatus.ssid || wifiRow?.ssid || "-",
        },
        {
          label: "IP Address",
          value: wifiIp || "-",
        },
        {
          label: "Signal",
          valueComponent: (
            <SignalValue value={wifiStatus.signalLevel || wifiRow?.signalStrength} />
          ),
        },
      ];
    }

    const tiles = [
      {
        label: "WAN Path",
        value: activeWanPathLabel,
        detail: activeWanPathDetail,
      },
      {
        label: "IP Address",
        value: frontEthernetIp || "-",
      },
      {
        label: "IP Assignment",
        value: formatAddressMode(config.ipAssign),
      },
    ];

    if (ethernetNeedsAttention || textIncludes(frontEthernetLink, ["down"])) {
      tiles.push({
        label: "Link",
        value: frontEthernetLink || ethernetReadiness,
      });
    }

    if (wifiConnected) {
      tiles.push({
        label: "WiFi Management",
        value: wifiStatus.ssid || wifiRow?.ssid || "Connected",
        detail: wifiIp,
      });
    }

    if (config.secondaryNetworkInterfaceMode === "bridge_mode") {
      tiles.push({
        label: "USB Ethernet",
        value: "Bridge enabled",
        detail: "Shares Front Ethernet",
      });
    }

    return tiles;
  })();

  const overviewConnections = [
    {
      row: frontEthernetRow,
      name: "Front Ethernet",
      role: hasLteFeature
        ? primaryIsLte
          ? "WAN fallback"
          : "WAN primary"
        : "WAN / management",
      status:
        routeEvidence !== "configured" && frontEthernetRow?.active
          ? frontEthernetRow.health || "Active"
          : ethernetReadiness,
      detail: buildShortInterfaceDetail({
        row: frontEthernetRow,
        config,
        wifiStatus,
        cellularStatus,
      }),
      active: routeEvidence !== "configured" && frontEthernetRow?.active,
    },
    hasLteFeature
      ? {
          row: lteRow,
          name: "LTE (lte0)",
          role: primaryIsLte ? "WAN primary" : "WAN fallback",
          status:
            routeEvidence !== "configured" && lteRow?.active
              ? lteRow.health || "Active"
              : lteReadiness,
          detail: buildShortInterfaceDetail({
            row: lteRow,
            config,
            wifiStatus,
            cellularStatus,
          }),
          active: routeEvidence !== "configured" && lteRow?.active,
        }
      : null,
    wifiAvailable || wifiConnected
      ? {
          row: wifiRow,
          name: "WiFi",
          role: wifiActive || activeRouteIsWifi ? "WAN path" : "Management",
          status: wifiActive || activeRouteIsWifi
            ? wifiRow?.health || "Active"
            : wifiConnected
              ? "Connected"
              : "Available",
          detail: buildShortInterfaceDetail({
            row: wifiRow,
            config,
            wifiStatus,
            cellularStatus,
          }),
          active: wifiActive || activeRouteIsWifi,
        }
      : null,
    config.secondaryNetworkInterfaceMode &&
    config.secondaryNetworkInterfaceMode !== "disabled"
      ? {
          row: usbEthernetRow,
          name: "USB Ethernet",
          role:
            config.secondaryNetworkInterfaceMode === "bridge_mode"
              ? "Bridge"
              : "Secondary interface",
          status:
            config.secondaryNetworkInterfaceMode === "bridge_mode"
              ? "Bridge enabled"
              : summarizeInterfaceReadiness(usbEthernetRow, "Configured"),
          detail: buildShortInterfaceDetail({
            row: usbEthernetRow || {
              interfaceName: "eth1",
              health: "",
              ipAddress: "",
            },
            config,
            wifiStatus,
            cellularStatus,
          }),
          active: routeEvidence !== "configured" && usbEthernetRow?.active,
        }
      : null,
  ].filter(Boolean);
  const showOverviewConnections = overviewConnections.length > 0;

  const renderLoadingPanel = (message = "Loading network information...") => (
    <div style={{ ...styles.banner, ...styles.infoBanner, marginBottom: 0 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Loading black size={14} />
        <span>{message}</span>
      </span>
    </div>
  );

  const renderFormField = (children) => (
    <div style={styles.formField}>{children}</div>
  );

  const renderOverviewTile = (tile) => (
    <div key={tile.label} style={styles.overviewTile}>
      <p style={styles.overviewTileLabel}>{tile.label}</p>
      <div style={styles.overviewTileValue}>
        {tile.valueComponent || valueOrDash(tile.value)}
      </div>
      {tile.detail ? (
        <p style={styles.overviewTileDetail}>{tile.detail}</p>
      ) : null}
    </div>
  );

  const renderOverviewConnection = (connection) => (
    <div key={connection.name} style={styles.connectionItem}>
      <div>
        <div style={styles.connectionName}>
          <span>{connection.name}</span>
          {connection.active ? <span style={styles.pill}>Active</span> : null}
        </div>
      </div>
      <div>
        <div style={styles.connectionRole}>{connection.role}</div>
        <div style={{ marginTop: 6 }}>
          <span
            style={{
              ...styles.pill,
              ...getHealthToneStyle(connection.status),
            }}
          >
            {valueOrDash(connection.status)}
          </span>
        </div>
      </div>
      <div style={styles.connectionDetail}>{valueOrDash(connection.detail)}</div>
    </div>
  );

  const renderOverviewSection = () => (
    <section style={{ ...styles.section, ...styles.firstSection }}>
      {/* <div style={styles.sectionHeader}>
        <div>
          <h5 style={styles.sectionTitle}>Network Overview</h5>
          <p style={styles.sectionDescription}>
            Current network posture and the interfaces that matter for this device.
          </p>
        </div>
      </div> */}
      {statusPending ? (
        renderLoadingPanel("Loading network overview...")
      ) : (
        <React.Fragment>
          <div style={styles.overviewHero}>
            <h5 style={styles.overviewHeroTitle}>{overviewHero.title}</h5>
            {overviewHero.detail ? (
              <p style={styles.overviewHeroDetail}>{overviewHero.detail}</p>
            ) : null}
          </div>
          <div style={styles.overviewTileGrid}>
            {overviewTiles.map(renderOverviewTile)}
          </div>
          {showOverviewConnections ? (
            <React.Fragment>
              <div style={styles.sectionHeader}>
                <div>
                  <h5 style={styles.sectionTitle}>Connections</h5>
                  <p style={styles.sectionDescription}>
                    Detailed IP, route, LTE, WiFi, and USB settings are available in the dedicated network tabs.
                  </p>
                </div>
              </div>
              {overviewConnections.length ? (
                <div style={styles.connectionList}>
                  {overviewConnections.map(renderOverviewConnection)}
                </div>
              ) : (
                <div style={{ ...styles.banner, ...styles.infoBanner }}>
                  No network interfaces reported.
                </div>
              )}
            </React.Fragment>
          ) : null}
        </React.Fragment>
      )}
    </section>
  );

  const renderWiredSection = () => (
    <React.Fragment>
      <section style={{ ...styles.section, ...styles.firstSection }}>
        <div style={styles.sectionHeader}>
          <div>
            <h5 style={styles.sectionTitle}>Wired Status</h5>
            <p style={styles.sectionDescription}>
              Front Ethernet is the standard management/WAN port. USB Ethernet can be disabled, bridged with the front port, or configured as a secondary interface.
            </p>
          </div>
        </div>
        {statusPending ? (
          renderLoadingPanel("Loading wired status...")
        ) : (
          <div style={styles.panelGrid}>
            <div style={styles.panel}>
              <div style={styles.panelHeader}>
                <h5 style={styles.panelTitle}>Front Ethernet</h5>
              </div>
              <RowList rows={frontEthernetStatusRows} />
            </div>
            <div style={styles.panel}>
              <div style={styles.panelHeader}>
                <h5 style={styles.panelTitle}>USB Ethernet</h5>
              </div>
              <RowList rows={usbEthernetStatusRows} />
            </div>
          </div>
        )}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h5 style={styles.sectionTitle}>Wired Configuration</h5>
            <p style={styles.sectionDescription}>
              Changes to these settings can restart the device network interfaces.
            </p>
          </div>
        </div>
        {confLoading ? (
          renderLoadingPanel("Loading wired configuration...")
        ) : (
          <React.Fragment>
            <div style={{ ...styles.configGroup, ...styles.firstConfigGroup }}>
              <div style={styles.sectionHeader}>
                <div>
                  <h5 style={styles.panelTitle}>Front Ethernet</h5>
                </div>
              </div>
              <div style={styles.formFields}>
                {renderFormField(
                  <TextInput
                    label="Hostname"
                    value={wiredForm.hostName}
                    callback={(value) =>
                      setWiredForm((current) => ({ ...current, hostName: value }))
                    }
                    disabled={!canModify || wiredSaving}
                    inputContainerStyle={{ marginBottom: 0 }}
                  />
                )}
                {renderFormField(
                  <SelectInput
                    label="IP Assignment"
                    value={wiredForm.ipAssign}
                    callback={(value) =>
                      setWiredForm((current) => ({ ...current, ipAssign: value }))
                    }
                    options={ipAssignOptions}
                    disabled={!canModify || wiredSaving}
                    defaultSelectText="Select assignment"
                    inputContainerStyle={{ marginBottom: 0 }}
                  />
                )}
                {wiredForm.ipAssign === "static" ? (
                  <React.Fragment>
                    {renderFormField(
                      <TextInput
                        label="Static IP"
                        value={wiredForm.staticIp}
                        callback={(value) =>
                          setWiredForm((current) => ({ ...current, staticIp: value }))
                        }
                        disabled={!canModify || wiredSaving}
                        inputContainerStyle={{ marginBottom: 0 }}
                      />
                    )}
                    {renderFormField(
                      <TextInput
                        label="Subnet Mask"
                        value={wiredForm.subnetMask}
                        callback={(value) =>
                          setWiredForm((current) => ({ ...current, subnetMask: value }))
                        }
                        disabled={!canModify || wiredSaving}
                        inputContainerStyle={{ marginBottom: 0 }}
                      />
                    )}
                    {renderFormField(
                      <TextInput
                        label="Gateway"
                        value={wiredForm.gatewayIp}
                        callback={(value) =>
                          setWiredForm((current) => ({ ...current, gatewayIp: value }))
                        }
                        disabled={!canModify || wiredSaving}
                        inputContainerStyle={{ marginBottom: 0 }}
                      />
                    )}
                  </React.Fragment>
                ) : null}
              </div>
            </div>
            <div style={styles.configGroup}>
              <div style={styles.sectionHeader}>
                <div>
                  <h5 style={styles.panelTitle}>USB Ethernet</h5>
                </div>
              </div>
              <div style={styles.formFields}>
                {renderFormField(
                  <SelectInput
                    label="Mode"
                    value={wiredForm.secondaryNetworkInterfaceMode}
                    callback={(value) =>
                      setWiredForm((current) => ({
                        ...current,
                        secondaryNetworkInterfaceMode: value,
                      }))
                    }
                    options={usbModeOptions}
                    disabled={!canModify || wiredSaving}
                    defaultSelectText="Select mode"
                    inputContainerStyle={{ marginBottom: 0 }}
                  />
                )}
                {wiredForm.secondaryNetworkInterfaceMode === "secondary_nic" ? (
                  <React.Fragment>
                    {renderFormField(
                      <TextInput
                        label="Secondary Hostname"
                        value={wiredForm.secondaryHostName}
                        callback={(value) =>
                          setWiredForm((current) => ({
                            ...current,
                            secondaryHostName: value,
                          }))
                        }
                        disabled={!canModify || wiredSaving}
                        inputContainerStyle={{ marginBottom: 0 }}
                      />
                    )}
                    {renderFormField(
                      <SelectInput
                        label="Secondary IP Assignment"
                        value={wiredForm.secondaryIpAssign}
                        callback={(value) =>
                          setWiredForm((current) => ({
                            ...current,
                            secondaryIpAssign: value,
                          }))
                        }
                        options={ipAssignOptions}
                        disabled={!canModify || wiredSaving}
                        defaultSelectText="Select assignment"
                        inputContainerStyle={{ marginBottom: 0 }}
                      />
                    )}
                    {wiredForm.secondaryIpAssign === "static" ? (
                      <React.Fragment>
                        {renderFormField(
                          <TextInput
                            label="Secondary Static IP"
                            value={wiredForm.secondaryStaticIp}
                            callback={(value) =>
                              setWiredForm((current) => ({
                                ...current,
                                secondaryStaticIp: value,
                              }))
                            }
                            disabled={!canModify || wiredSaving}
                            inputContainerStyle={{ marginBottom: 0 }}
                          />
                        )}
                        {renderFormField(
                          <TextInput
                            label="Secondary Subnet Mask"
                            value={wiredForm.secondarySubnetMask}
                            callback={(value) =>
                              setWiredForm((current) => ({
                                ...current,
                                secondarySubnetMask: value,
                              }))
                            }
                            disabled={!canModify || wiredSaving}
                            inputContainerStyle={{ marginBottom: 0 }}
                          />
                        )}
                        {renderFormField(
                          <TextInput
                            label="Secondary Gateway"
                            value={wiredForm.secondaryGatewayIp}
                            callback={(value) =>
                              setWiredForm((current) => ({
                                ...current,
                                secondaryGatewayIp: value,
                              }))
                            }
                            disabled={!canModify || wiredSaving}
                            inputContainerStyle={{ marginBottom: 0 }}
                          />
                        )}
                      </React.Fragment>
                    ) : null}
                  </React.Fragment>
                ) : null}
              </div>
            </div>
            <div style={styles.formActions}>
              <button
                type="button"
                style={styles.button}
                onClick={handleSaveWiredSettings}
                disabled={!canModify || wiredSaving}
              >
                {wiredSaving ? "Saving..." : "Save Wired Settings"}
              </button>
            </div>
          </React.Fragment>
        )}
      </section>
    </React.Fragment>
  );

  const renderLteSection = () => (
    <section style={{ ...styles.section, ...styles.firstSection }}>
      {/* <div style={styles.sectionHeader}>
        <div>
          <h5 style={styles.sectionTitle}>LTE</h5>
          <p style={styles.sectionDescription}>
            Cellular WAN status and failover settings. LTE IP addresses are not shown.
          </p>
        </div>
      </div> */}
      {statusPending ? (
        renderLoadingPanel("Loading LTE status...")
      ) : !hasLteFeature ? (
        <div style={{ ...styles.banner, ...styles.infoBanner }}>
          LTE configuration is not available for this PDU firmware.
        </div>
      ) : (
        <div style={styles.panelGrid}>
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <h5 style={styles.panelTitle}>LTE Status</h5>
            </div>
            {networkStatus?.cellular?.available === false ? (
              <div style={{ ...styles.banner, ...styles.infoBanner }}>
                {networkStatus.cellular.message ||
                  "Cellular runtime status is not available on this firmware."}
              </div>
            ) : null}
            <RowList rows={lteStatusRows} />
          </div>
          <div style={styles.formPanel}>
            <h5 style={styles.panelTitle}>LTE Settings</h5>
            {renderFormField(
              <ToggleInput
                label="Enable Cellular"
                value={lteForm.enableCellular}
                callback={(value) =>
                  setLteForm((current) => ({
                    ...current,
                    enableCellular: value,
                  }))
                }
                toggleLabelOff="Disabled"
                toggleLabelOn="Enabled"
                disabled={!canModify || lteSaving}
                inputContainerStyle={{ marginBottom: 0 }}
              />
            )}
            {renderFormField(
              <TextInput
                label="APN"
                value={lteForm.cellularApn}
                callback={(value) =>
                  setLteForm((current) => ({ ...current, cellularApn: value }))
                }
                disabled={!canModify || lteSaving || !lteForm.enableCellular}
                inputContainerStyle={{ marginBottom: 0 }}
              />
            )}
            {renderFormField(
              <SelectInput
                label="Failover Priority"
                value={lteForm.ltePolicy}
                callback={(value) =>
                  setLteForm((current) => ({ ...current, ltePolicy: value }))
                }
                options={ltePolicyOptions}
                disabled={!canModify || lteSaving || !lteForm.enableCellular}
                defaultSelectText="Select priority"
                inputContainerStyle={{ marginBottom: 0 }}
              />
            )}
            <div style={styles.grid}>
              {renderFormField(
                <TextInput
                  label="Primary DNS"
                  value={lteForm.primaryDns}
                  callback={(value) =>
                    setLteForm((current) => ({ ...current, primaryDns: value }))
                  }
                  disabled={!canModify || lteSaving || !lteForm.enableCellular}
                  inputContainerStyle={{ marginBottom: 0 }}
                />
              )}
              {renderFormField(
                <TextInput
                  label="Secondary DNS"
                  value={lteForm.secondaryDns}
                  callback={(value) =>
                    setLteForm((current) => ({
                      ...current,
                      secondaryDns: value,
                    }))
                  }
                  disabled={!canModify || lteSaving || !lteForm.enableCellular}
                  inputContainerStyle={{ marginBottom: 0 }}
                />
              )}
            </div>
            <div style={styles.formActions}>
              <button
                type="button"
                style={styles.button}
                onClick={handleSaveLteSettings}
                disabled={!canModify || lteSaving}
              >
                {lteSaving ? "Saving..." : "Save LTE Settings"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );

  const renderWifiSection = () => (
    <section style={{ ...styles.section, ...styles.firstSection }}>
      {/* <div style={styles.sectionHeader}>
        <div>
          <h5 style={styles.sectionTitle}>WiFi Management</h5>
          <p style={styles.sectionDescription}>
            WiFi is managed independently from LTE failover.
          </p>
        </div>
      </div> */}
      {statusPending ? (
        renderLoadingPanel("Loading WiFi status...")
      ) : !wifiAvailable ? (
        <div style={{ ...styles.banner, ...styles.infoBanner }}>
          {networkStatus?.wifi?.message ||
            "WiFi management is not available for this firmware."}
        </div>
      ) : (
        <div style={styles.panelGrid}>
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <h5 style={styles.panelTitle}>WiFi Status</h5>
              <button
                type="button"
                style={styles.dangerButton}
                onClick={handleWifiDisconnect}
                disabled={!canModify || wifiLoading || !wifiStatus.connected}
              >
                Disconnect
              </button>
            </div>
            <RowList rows={wifiStatusRows} />
          </div>
          <div style={styles.formPanel}>
            <h5 style={styles.panelTitle}>WiFi Connection</h5>
            {renderFormField(
              <ToggleInput
                label="WiFi Radio"
                value={wifiRadioEnabled}
                callback={handleWifiRadio}
                toggleLabelOff="Disabled"
                toggleLabelOn="Enabled"
                disabled={!canModify || wifiLoading}
                inputContainerStyle={{ marginBottom: 0 }}
              />
            )}
            {renderFormField(
              <div style={styles.compactActions}>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={handleWifiScan}
                  disabled={!canModify || wifiLoading || !wifiRadioEnabled}
                >
                  {wifiLoading ? "Working..." : "Scan for Networks"}
                </button>
                <span style={styles.muted}>
                  {wifiNetworks.length
                    ? `${wifiNetworks.length} network${wifiNetworks.length === 1 ? "" : "s"} found`
                    : "Scan to discover nearby SSIDs"}
                </span>
              </div>
            )}
            {renderFormField(
              <TextInput
                label="SSID"
                value={wifiSsid}
                callback={setWifiSsid}
                placeholder="Network name or hidden SSID"
                disabled={!canModify || wifiLoading || !wifiRadioEnabled}
                inputContainerStyle={{ marginBottom: 0 }}
              />
            )}
            {renderFormField(
              <TextInput
                label="Password"
                type="password"
                value={wifiPassword}
                callback={setWifiPassword}
                placeholder="WiFi password"
                disabled={!canModify || wifiLoading || !wifiRadioEnabled}
                inputContainerStyle={{ marginBottom: 0 }}
              />
            )}
            {selectedWifiNetwork ? (
              <RowList
                rows={[
                  { label: "Selected Security", value: valueOrDash(selectedWifiSecurity) },
                  { label: "Selected Band", value: valueOrDash(selectedWifiBand) },
                  {
                    label: "Selected Signal",
                    valueComponent: (
                      <SignalValue value={selectedWifiNetwork.signalLevel} />
                    ),
                  },
                  { label: "Selected BSSID", value: valueOrDash(selectedWifiNetwork.bssid) },
                ]}
              />
            ) : null}
            <div style={styles.formActions}>
              <button
                type="button"
                style={styles.button}
                onClick={handleWifiConnect}
                disabled={!canModify || wifiLoading || !wifiRadioEnabled}
              >
                Join Selected Network
              </button>
            </div>
            {wifiNetworks.length > 0 ? (
              <div style={styles.scanList}>
                {wifiNetworks.map((network, index) => (
                  <button
                    key={`${network.ssid || "hidden"}-${network.bssid || index}`}
                    type="button"
                    style={styles.scanItem}
                    onClick={() => setWifiSsid(network.ssid || "")}
                    disabled={!canModify || wifiLoading || !network.ssid}
                  >
                    <div>{network.ssid || "Hidden network"}</div>
                    <div style={styles.muted}>
                      {[
                        formatSignalValue(network.signalLevel),
                        frequencyToBand(network.frequency),
                        formatSecurityFromFlags(network.flags),
                        network.frequency ? `${network.frequency} MHz` : "",
                      ]
                        .filter((part) => part && part !== "-")
                        .join(" | ")}
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );

  const renderNetworkView = () => {
    if (networkView === NETWORK_VIEW.WIRED) return renderWiredSection();
    if (networkView === NETWORK_VIEW.LTE) return renderLteSection();
    if (networkView === NETWORK_VIEW.WIFI) return renderWifiSection();
    return renderOverviewSection();
  };

  return (
    <div style={styles.wrap}>
      {!hideHeader ? (
        <div style={styles.header}>
          <div>
            <h4 style={styles.title}>{section?.label || "Network"}</h4>
            {section?.description ? (
              <p style={styles.description}>{section.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            style={styles.refreshButton}
            disabled={confLoading || networkLoading}
            aria-label="Refresh network information"
            title="Refresh network information"
          >
            {confLoading || networkLoading ? (
              <Loading black size={18} />
            ) : (
              <i className="material-icons">refresh</i>
            )}
          </button>
        </div>
      ) : null}

      {deviceOffline ? (
        <div style={{ ...styles.banner, ...styles.warningBanner }}>
          {offlineReason ||
            "Device is offline. Displaying the latest known network information."}
        </div>
      ) : null}

      {networkUnavailable ? (
        <div style={{ ...styles.banner, ...styles.infoBanner }}>
          Live network status is not available for this firmware/API version.
        </div>
      ) : null}

      {networkError ? (
        <div style={{ ...styles.banner, ...styles.errorBanner }}>
          {networkError}
        </div>
      ) : null}

      {renderNetworkView()}
    </div>
  );
}
